import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { DiffsListTab } from '@/components/diffs/DiffsListTab';
import { DiffsReportsTab } from '@/components/diffs/DiffsReportsTab';
import { useAvailablePeriods } from '@/hooks/useClaudeTasks';
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
// @ts-ignore
import Cookies from 'js-cookie';

const Diffs = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState<string>(searchParams.get('tab') || 'lists');

  // Period filter shared between both tabs
  const [periodFilter, setPeriodFilter] = useState<string>(() => {
    const periodsParam = searchParams.get('periods');
    if (periodsParam) {
      return periodsParam.split(',')[0];
    }
    const savedPeriod = Cookies.get('diffs_period_filter');
    return savedPeriod || '';
  });

  // Published filter shared between tabs
  const [publishedFilter, setPublishedFilter] = useState<string>(() => {
    const saved = Cookies.get('diffs_published_filter');
    return saved || 'all';
  });

  const handlePublishedFilterChange = (value: string) => {
    setPublishedFilter(value);
    if (value && value !== 'all') {
      Cookies.set('diffs_published_filter', value, { expires: 30 });
    } else {
      Cookies.remove('diffs_published_filter');
    }
  };

  // Fetch available periods
  const { data: availablePeriods } = useAvailablePeriods();

  const handlePeriodToggle = (period: string) => {
    setPeriodFilter(prev => {
      const newFilter = prev === period ? '' : period;
      if (newFilter) {
        Cookies.set('diffs_period_filter', newFilter, { expires: 30 });
      } else {
        Cookies.remove('diffs_period_filter');
      }
      return newFilter;
    });
  };

  const handleTabChange = (value: string) => {
    setActiveTab(value);
    // Preserve all existing search params, only update tab
    setSearchParams(prev => {
      const params = new URLSearchParams(prev);
      params.set('tab', value);
      return params;
    });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-[1400px] mx-auto px-8">
        <div className="py-8">
          {/* Two-tab layout */}
          <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
            <div className="mb-6">
              <TabsList className="mb-4">
                <TabsTrigger value="lists">Lists</TabsTrigger>
                <TabsTrigger value="reports">Reports</TabsTrigger>
              </TabsList>

              {/* Period Filter - Shared across tabs */}
              {availablePeriods && availablePeriods.length > 0 && (() => {
                const weeks = availablePeriods.filter(p => p.includes('W'));
                const quarters = availablePeriods.filter(p => p.includes('Q'));
                const years = availablePeriods.filter(p => p.includes('Y'));
                const recentWeeks = weeks.slice(0, 1);
                const olderWeeks = weeks.slice(1);
                const recentQuarters = quarters.slice(0, 4);
                const olderQuarters = quarters.slice(4);
                const recentYears = years.slice(0, 3);
                const olderYears = years.slice(3);

                return (
                  <div className="mb-4 pb-4 border-b">
                    <div className="flex flex-wrap gap-2 items-center">
                      {recentWeeks.map(period => (
                        <Badge
                          key={period}
                          variant={periodFilter === period ? 'default' : 'outline'}
                          className="cursor-pointer"
                          onClick={() => handlePeriodToggle(period)}
                        >
                          {period}
                        </Badge>
                      ))}

                      {olderWeeks.length > 0 && (
                        <Select
                          value={olderWeeks.includes(periodFilter) ? periodFilter : ""}
                          onValueChange={(value) => {
                            if (value) {
                              handlePeriodToggle(value);
                            }
                          }}
                        >
                          <SelectTrigger className="h-6 w-auto gap-1 border-0 bg-transparent p-0 hover:bg-transparent focus:ring-0 focus:ring-offset-0 [&>svg]:h-3 [&>svg]:w-3 [&>svg]:opacity-50">
                            <Badge variant={olderWeeks.includes(periodFilter) ? 'default' : 'outline'} className="cursor-pointer">
                              {olderWeeks.includes(periodFilter) ? periodFilter : `+${olderWeeks.length} more`}
                            </Badge>
                          </SelectTrigger>
                          <SelectContent>
                            {olderWeeks.map(period => (
                              <SelectItem key={period} value={period}>
                                {period}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}

                      {weeks.length > 0 && quarters.length > 0 && (
                        <span className="text-gray-300 mx-1">|</span>
                      )}

                      {recentQuarters.map(period => (
                        <Badge
                          key={period}
                          variant={periodFilter === period ? 'default' : 'outline'}
                          className="cursor-pointer"
                          onClick={() => handlePeriodToggle(period)}
                        >
                          {period}
                        </Badge>
                      ))}

                      {olderQuarters.length > 0 && (
                        <Select
                          value={olderQuarters.includes(periodFilter) ? periodFilter : ""}
                          onValueChange={(value) => {
                            if (value) {
                              handlePeriodToggle(value);
                            }
                          }}
                        >
                          <SelectTrigger className="h-6 w-auto gap-1 border-0 bg-transparent p-0 hover:bg-transparent focus:ring-0 focus:ring-offset-0 [&>svg]:h-3 [&>svg]:w-3 [&>svg]:opacity-50">
                            <Badge variant={olderQuarters.includes(periodFilter) ? 'default' : 'outline'} className="cursor-pointer">
                              {olderQuarters.includes(periodFilter) ? periodFilter : `+${olderQuarters.length} more`}
                            </Badge>
                          </SelectTrigger>
                          <SelectContent>
                            {olderQuarters.map(period => (
                              <SelectItem key={period} value={period}>
                                {period}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}

                      {(quarters.length > 0 || weeks.length > 0) && years.length > 0 && (
                        <span className="text-gray-300 mx-1">|</span>
                      )}

                      {recentYears.map(period => (
                        <Badge
                          key={period}
                          variant={periodFilter === period ? 'default' : 'outline'}
                          className="cursor-pointer"
                          onClick={() => handlePeriodToggle(period)}
                        >
                          {period.replace('Y', '')}
                        </Badge>
                      ))}

                      {olderYears.length > 0 && (
                        <Select
                          value={olderYears.includes(periodFilter) ? periodFilter : ""}
                          onValueChange={(value) => {
                            if (value) {
                              handlePeriodToggle(value);
                            }
                          }}
                        >
                          <SelectTrigger className="h-6 w-auto gap-1 border-0 bg-transparent p-0 hover:bg-transparent focus:ring-0 focus:ring-offset-0 [&>svg]:h-3 [&>svg]:w-3 [&>svg]:opacity-50">
                            <Badge variant={olderYears.includes(periodFilter) ? 'default' : 'outline'} className="cursor-pointer">
                              {olderYears.includes(periodFilter) ? periodFilter.replace('Y', '') : `+${olderYears.length} more`}
                            </Badge>
                          </SelectTrigger>
                          <SelectContent>
                            {olderYears.map(period => (
                              <SelectItem key={period} value={period}>
                                {period.replace('Y', '')}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}

                      {(weeks.length > 0 || quarters.length > 0 || years.length > 0) && (
                        <span className="text-gray-300 mx-1">|</span>
                      )}

                      <Badge
                        variant={periodFilter === '' ? 'default' : 'outline'}
                        className="cursor-pointer"
                        onClick={() => handlePeriodToggle('')}
                      >
                        All
                      </Badge>
                    </div>

                    {/* Published filter badges */}
                    <div className="flex gap-2 items-center mt-2">
                      <Badge
                        variant={publishedFilter === 'all' ? 'default' : 'outline'}
                        className="cursor-pointer"
                        onClick={() => handlePublishedFilterChange('all')}
                      >
                        All
                      </Badge>
                      <Badge
                        variant="outline"
                        className={`cursor-pointer ${publishedFilter === 'published' ? 'bg-green-600 text-white border-green-600 hover:bg-green-700' : 'text-gray-600'}`}
                        onClick={() => handlePublishedFilterChange('published')}
                      >
                        Published
                      </Badge>
                      <Badge
                        variant="outline"
                        className={`cursor-pointer ${publishedFilter === 'unpublished' ? 'bg-gray-500 text-white border-gray-500 hover:bg-gray-600' : 'text-gray-600'}`}
                        onClick={() => handlePublishedFilterChange('unpublished')}
                      >
                        Draft
                      </Badge>
                    </div>
                  </div>
                );
              })()}
            </div>

            <TabsContent value="lists">
              <DiffsListTab periodFilter={periodFilter} onPeriodChange={(period) => handlePeriodToggle(period)} publishedFilter={publishedFilter} onPublishedFilterChange={handlePublishedFilterChange} />
            </TabsContent>

            <TabsContent value="reports">
              <DiffsReportsTab periodFilter={periodFilter} />
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
};

export default Diffs;
